import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/** The nine-dot grid that opens the launcher. */
function GridGlyph() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="currentColor"
    >
      {[5, 12, 19].map((cy) =>
        [5, 12, 19].map((cx) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="2" />
        )),
      )}
    </svg>
  );
}

function GearGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="3.5" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.2 5.2l2.1 2.1M16.7 16.7l2.1 2.1M18.8 5.2l-2.1 2.1M7.3 16.7l-2.1 2.1" />
    </svg>
  );
}

const TILE =
  "flex w-24 flex-col items-center gap-2 rounded-lg px-2 py-3 text-center text-xs text-slate-700 hover:bg-slate-100";

function Tile({
  to,
  href,
  label,
  children,
  onPick,
}: {
  to?: string;
  href?: string;
  label: string;
  children: React.ReactNode;
  onPick: () => void;
}) {
  const body = (
    <>
      {children}
      <span className="w-full truncate">{label}</span>
    </>
  );
  // A product this person only uses opens the product itself; one they staff opens its console.
  return href ? (
    <a className={TILE} href={href} onClick={onPick}>
      {body}
    </a>
  ) : (
    <Link className={TILE} to={to ?? "/account"} onClick={onPick}>
      {body}
    </Link>
  );
}

/** One tile in the launcher: a product this person uses, staffs, or both. */
export interface LauncherProduct {
  slug: string;
  name: string;
  logo_url: string | null;
  /** The console workspace, when they staff it. */
  to?: string;
  /** The product's own front door, when they only use it. */
  href?: string;
}

/** A product's own logo, or its initial in the brand circle when it has none. */
function ProductMark({ product }: { product: LauncherProduct }) {
  if (product.logo_url) {
    return (
      <img src={product.logo_url} alt="" className="h-10 w-10 rounded-lg" />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand text-base font-semibold text-white"
    >
      {product.name.charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * The app launcher (docs/15 §1): Personal, every product this person may open, and Platform for
 * an owner — as tiles, the way an app grid works, rather than a dropdown of words.
 */
export function AppLauncher({
  products,
  showPlatform,
  onConsole,
}: {
  products: LauncherProduct[];
  showPlatform: boolean;
  onConsole: boolean;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        aria-label="Intelibo products"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className={`flex h-9 w-9 items-center justify-center rounded-full ${
          onConsole
            ? "text-white/90 hover:bg-white/15"
            : "text-slate-600 hover:bg-slate-100"
        }`}
      >
        <GridGlyph />
      </button>
      {open ? (
        <div className="absolute right-0 z-20 mt-2 w-72 rounded-lg border border-slate-200 bg-white p-3 shadow-card-lg">
          <div className="flex flex-wrap justify-start gap-1">
            {products.map((product) => (
              <Tile
                key={product.slug}
                to={product.to}
                href={product.href}
                label={product.name}
                onPick={() => setOpen(false)}
              >
                <ProductMark product={product} />
              </Tile>
            ))}
            {products.length === 0 ? (
              <p className="px-3 py-2 text-sm text-slate-500">
                No products yet.
              </p>
            ) : null}
          </div>
          {/* Administration is not a product, so it sits below the grid, on its own. */}
          {showPlatform ? (
            <Link
              to="/admin/platform"
              onClick={() => setOpen(false)}
              className="mt-2 flex items-center gap-3 rounded-lg border-t border-slate-100 px-3 pb-1 pt-3 text-sm text-slate-700 hover:bg-slate-100"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-dark text-white">
                <GearGlyph />
              </span>
              Intelibo admin
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
